import type { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, Plus, TicketPercent } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { usePermissions } from '@/features/auth';
import { useMarketTimeZone, useSelectedCountry } from '@/features/country-scope';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';
import { CopyButton } from '@/shared/components/copy-button';
import { DataTable } from '@/shared/components/data-table/data-table';
import { DataTablePagination } from '@/shared/components/data-table/data-table-pagination';
import { DataTableToolbar } from '@/shared/components/data-table/data-table-toolbar';
import { useDataTable } from '@/shared/components/data-table/use-data-table';
import { EmptyState } from '@/shared/components/empty-state';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';
import { SearchInput } from '@/shared/components/search-input';
import { StatusBadge } from '@/shared/components/status-badge';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';
import { useTableSearchParams } from '@/shared/hooks/use-table-search-params';
import { formatCurrency, formatDateTime } from '@/shared/lib/format';

import { useCoupons, useSetCouponActive } from '../api/coupons.queries';
import { CouponDialog } from '../components/coupon-dialog';
import { CouponRedemptionsDialog } from '../components/coupon-redemptions-dialog';
import { COUPON_STATUS, formatCouponScope, formatCouponValue } from '../lib/coupon-format';
import type { Coupon, CouponKind, CouponStatus } from '../types/coupons.types';

const ANY = 'any';
const KINDS: { value: CouponKind; label: string }[] = [
  { value: 'Public', label: 'Every customer' },
  { value: 'Personal', label: 'One customer' },
  { value: 'Points', label: 'Bought with points' },
];
const KIND_LABEL: Record<CouponKind, string> = {
  Public: 'Every customer',
  Personal: 'One customer',
  Points: 'Points',
};

type Open =
  | { kind: 'create' }
  | { kind: 'edit'; coupon: Coupon }
  | { kind: 'uses'; coupon: Coupon }
  | { kind: 'status'; coupon: Coupon }
  | null;

function columns(
  canManage: boolean,
  setOpen: (open: Open) => void,
  zone: string,
): ColumnDef<Coupon, unknown>[] {
  const base: ColumnDef<Coupon, unknown>[] = [
    {
      id: 'code',
      header: 'Code',
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <span className="font-mono font-medium">{row.original.code}</span>
          <CopyButton value={row.original.code} label={`coupon ${row.original.code}`} />
        </div>
      ),
    },
    {
      id: 'kind',
      header: 'For',
      enableSorting: false,
      cell: ({ row }) =>
        row.original.customerId ? (
          <div>
            <span>{KIND_LABEL[row.original.kind]}</span>
            <Link
              to={`/customers/${row.original.customerId}`}
              className="text-primary block text-[11px] hover:underline"
            >
              {row.original.customerName ?? `#${row.original.customerId}`}
            </Link>
          </div>
        ) : (
          KIND_LABEL[row.original.kind]
        ),
    },
    {
      id: 'value',
      header: 'Value',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <span className="tabular-nums">
            {formatCouponValue(row.original, row.original.currency)}
          </span>
          {row.original.marginWarning && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-status-warning" aria-label={row.original.marginWarning}>
                  <AlertTriangle className="size-3.5" aria-hidden />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs">
                {row.original.marginWarning}
              </TooltipContent>
            </Tooltip>
          )}
          {row.original.minOrderAmount !== null && (
            <span className="text-muted-foreground block text-[11px]">
              min{' '}
              {formatCurrency(row.original.minOrderAmount, row.original.currency ?? undefined, {
                precise: true,
              })}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'scope',
      header: 'Brands',
      enableSorting: false,
      cell: ({ row }) => <span className="line-clamp-2">{formatCouponScope(row.original)}</span>,
    },
    {
      id: 'validity',
      header: 'Valid',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {formatDateTime(row.original.validFrom, zone)}
          <span className="block">→ {formatDateTime(row.original.validTo, zone)}</span>
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => (
        <StatusBadge variant={COUPON_STATUS[row.original.status].variant}>
          {COUPON_STATUS[row.original.status].label}
        </StatusBadge>
      ),
    },
    {
      id: 'uses',
      header: 'Uses',
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="link"
          className="h-auto p-0 text-xs"
          onClick={() => setOpen({ kind: 'uses', coupon: row.original })}
          aria-label={`Uses of ${row.original.code}`}
        >
          {row.original.redeemedCount} used
          {row.original.reservedCount > 0 && ` · ${row.original.reservedCount} in progress`}
          {row.original.conflictCount > 0 && ` · ${row.original.conflictCount} conflict`}
        </Button>
      ),
    },
  ];
  if (!canManage) return base;
  return [
    ...base,
    {
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setOpen({ kind: 'edit', coupon: row.original })}
            aria-label={`Edit ${row.original.code}`}
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setOpen({ kind: 'status', coupon: row.original })}
            aria-label={`${row.original.isActive ? 'Deactivate' : 'Activate'} ${row.original.code}`}
          >
            {row.original.isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </div>
      ),
    },
  ];
}

/**
 * Marketing › Coupons: personal and public coupons of the selected country, and the points
 * coupons customers bought. Read with coupons.read, changed with coupons.manage. A coupon takes a
 * percent off covered items (optionally capped), once per customer and one per checkout.
 */
export function CouponsPage() {
  const { country } = useSelectedCountry();
  const zone = useMarketTimeZone();
  const { can } = usePermissions();
  const canManage = can('coupons.manage');
  const tableState = useTableSearchParams();
  const search = useDebouncedValue(tableState.search, 300);
  const kindParam = tableState.getFilter('kind') ?? ANY;
  const statusParam = tableState.getFilter('status') ?? ANY;
  const kind = KINDS.some((k) => k.value === kindParam) ? (kindParam as CouponKind) : undefined;
  const status = statusParam in COUPON_STATUS ? (statusParam as CouponStatus) : undefined;
  const [open, setOpen] = useState<Open>(null);
  const setActive = useSetCouponActive();

  const params = useMemo(
    () => ({
      page: tableState.page,
      pageSize: tableState.pageSize,
      search: search || undefined,
      kind,
      status,
    }),
    [tableState.page, tableState.pageSize, search, kind, status],
  );
  const coupons = useCoupons(country?.id, params);
  const tableColumns = useMemo(() => columns(canManage, setOpen, zone), [canManage, zone]);
  const table = useDataTable({
    data: coupons.data,
    columns: tableColumns,
    tableState,
    getRowId: (c) => String(c.id),
  });
  const countryName = country ? (country.nameEn ?? country.code ?? `#${country.id}`) : '';
  const currency = coupons.data?.items[0]?.currency ?? null;
  const statusTarget = open?.kind === 'status' ? open.coupon : null;

  return (
    <PageContainer>
      <PageHeader
        title="Coupons"
        description="Percent-off codes for one customer or for everyone, on every brand or chosen brands. Points coupons are bought by customers from the points tiers."
        actions={
          canManage && country ? (
            <Button size="sm" onClick={() => setOpen({ kind: 'create' })}>
              <Plus className="size-3.5" aria-hidden />
              New coupon
            </Button>
          ) : null
        }
      />
      {!country ? (
        <EmptyState
          icon={TicketPercent}
          title="Choose a country"
          description="Coupons are set per country."
        />
      ) : (
        <div className="flex flex-col gap-3">
          <DataTableToolbar
            hasActiveFilters={tableState.hasActiveFilters}
            onResetFilters={tableState.resetFilters}
          >
            <SearchInput
              value={tableState.search}
              onChange={tableState.setSearch}
              placeholder="Search code, customer name or mobile…"
              className="w-full sm:w-72"
            />
            <Select
              value={kindParam}
              onValueChange={(value) => tableState.setFilter('kind', value)}
            >
              <SelectTrigger
                size="sm"
                className="h-8 w-40 text-xs"
                aria-label="Filter by who it is for"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY} className="text-xs">
                  Anyone
                </SelectItem>
                {KINDS.map((k) => (
                  <SelectItem key={k.value} value={k.value} className="text-xs">
                    {k.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={statusParam}
              onValueChange={(value) => tableState.setFilter('status', value)}
            >
              <SelectTrigger size="sm" className="h-8 w-36 text-xs" aria-label="Filter by status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY} className="text-xs">
                  Any status
                </SelectItem>
                {(Object.keys(COUPON_STATUS) as CouponStatus[]).map((s) => (
                  <SelectItem key={s} value={s} className="text-xs">
                    {COUPON_STATUS[s].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableToolbar>
          <DataTable
            table={table}
            isLoading={coupons.isLoading}
            isFetching={coupons.isFetching}
            error={coupons.error}
            onRetry={() => void coupons.refetch()}
            emptyState={
              tableState.hasActiveFilters ? (
                <EmptyState
                  icon={TicketPercent}
                  title="No coupons match"
                  description="Try another search, kind or status."
                  action={
                    <Button variant="outline" size="sm" onClick={tableState.resetFilters}>
                      Clear filters
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={TicketPercent}
                  title="No coupons yet"
                  description={`No coupon has been created for ${countryName}.`}
                />
              )
            }
          />
          <DataTablePagination table={table} />
        </div>
      )}

      {country && open?.kind === 'create' && (
        <CouponDialog
          countryId={country.id}
          countryName={countryName}
          currency={currency}
          onClose={() => setOpen(null)}
        />
      )}
      {country && open?.kind === 'edit' && (
        <CouponDialog
          countryId={country.id}
          countryName={countryName}
          currency={open.coupon.currency}
          coupon={open.coupon}
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === 'uses' && (
        <CouponRedemptionsDialog coupon={open.coupon} onClose={() => setOpen(null)} />
      )}
      <ConfirmDialog
        open={statusTarget !== null}
        onOpenChange={(isOpen) => !isOpen && setOpen(null)}
        title={
          statusTarget?.isActive
            ? `Deactivate ${statusTarget.code}?`
            : `Activate ${statusTarget?.code ?? ''}?`
        }
        description={
          statusTarget?.isActive
            ? 'Customers can no longer use it from now on. Payments already in progress keep their discount. You can activate it again.'
            : 'Customers can use it again within its dates.'
        }
        confirmLabel={statusTarget?.isActive ? 'Deactivate' : 'Activate'}
        variant={statusTarget?.isActive ? 'destructive' : 'default'}
        isPending={setActive.isPending}
        onConfirm={() =>
          statusTarget &&
          setActive.mutate(
            { id: statusTarget.id, active: !statusTarget.isActive },
            { onSuccess: () => setOpen(null) },
          )
        }
      />
    </PageContainer>
  );
}
